import { TAppNameEnum } from '@onesource/schemas';
import { getSession, useHeartbeatQuery } from '@onesource/services';
import { createContext, FC, ReactNode, useContext, useEffect, useMemo } from 'react';
import { WhoIsOnline } from './who-is-online-collection';

type TUserOnlineContext = {
  isLoading: boolean;
};

type TProps = {
  app: TAppNameEnum;
  children: ReactNode;
};

const UserOnlineContext = createContext<TUserOnlineContext | undefined>(undefined);

export const UserOnlineProvider: FC<TProps> = ({ app, children }) => {
  const token = getSession();

  const { data: heartbeatData, isLoading } = useHeartbeatQuery({
    appName: app,
    enabled: !!token,
    refetchInterval: () => WhoIsOnline.getHeartbeatInterval(),
    retry: 3,
  });

  useEffect(() => {
    if (heartbeatData) {
      WhoIsOnline.updateFromHeartbeat(heartbeatData);
    }
  }, [heartbeatData]);

  useEffect(() => {
    if (!token) {
      WhoIsOnline.clear();
    }
  }, [token]);

  const value = useMemo<TUserOnlineContext>(() => ({ isLoading }), [isLoading]);

  return <UserOnlineContext.Provider value={value}>{children}</UserOnlineContext.Provider>;
};

export const useUserOnlineContext = (): TUserOnlineContext => {
  const context = useContext(UserOnlineContext);

  if (context === undefined) {
    throw new Error('useUserOnlineContext must be used within a UserOnlineProvider');
  }

  return context;
};
